import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Monitor } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useHomey } from "@/hooks/useHomey";
import { normalizeKioskSettings } from "@/lib/kiosk";
import type { KioskSettings } from "@/types/homey";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NONE_VALUE = "__none__";

const IDLE_PRESETS: { value: number; label: string }[] = [
  { value: 30, label: "30 seconds" },
  { value: 60, label: "1 minute" },
  { value: 120, label: "2 minutes" },
  { value: 300, label: "5 minutes" },
  { value: 600, label: "10 minutes" },
];

/** Capability ids that mark a device as a usable wake sensor. */
function isWakeSensorCapability(capId: string): boolean {
  return capId === "alarm_motion" || capId === "alarm_contact" || capId.includes("presence");
}

/**
 * KioskSettingsSection — "Kiosk & display" settings card.
 *
 * Loads the persisted kiosk preferences (getDashboardSettings -> normalize),
 * lets the user edit them, and saves via setKioskSettings. The wake-sensor
 * picker is built from the live Homey snapshot, listing devices that expose a
 * motion / contact / presence capability, plus a "None" option.
 *
 * To test: change the idle timeout / screensaver style / wake sensor and press
 * Save; reload Settings and confirm the values persist.
 */
export function KioskSettingsSection() {
  const utils = trpc.useUtils();
  const { snapshot } = useHomey();
  const { data: settingsRow } = trpc.homey.getDashboardSettings.useQuery();

  const persisted = useMemo<KioskSettings>(
    () => normalizeKioskSettings(settingsRow?.kiosk),
    [settingsRow],
  );

  const [draft, setDraft] = useState<KioskSettings>(persisted);

  // Sync the local draft when the persisted value first loads / changes.
  useEffect(() => {
    setDraft(persisted);
  }, [persisted]);

  // Devices that can act as a wake sensor (motion / contact / presence).
  const wakeSensorDevices = useMemo(() => {
    if (!snapshot) return [];
    return snapshot.devices
      .filter((d) => d.capabilities.some(isWakeSensorCapability))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [snapshot]);

  const save = trpc.homey.setKioskSettings.useMutation({
    onSuccess: () => {
      void utils.homey.getDashboardSettings.invalidate();
      toast.success("Kiosk settings saved");
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Card data-slot="kiosk-settings-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Monitor className="size-4" /> Kiosk &amp; display
        </CardTitle>
        <CardDescription>
          Turn this dashboard into an always-on wall display: an idle screensaver, presence wake,
          and a fullscreen kiosk toggle.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {/* Enabled */}
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <Label htmlFor="kiosk-enabled">Screensaver</Label>
            <p className="text-sm text-muted-foreground">
              Show an idle screensaver after a period of inactivity.
            </p>
          </div>
          <Switch
            id="kiosk-enabled"
            checked={draft.enabled}
            onCheckedChange={(enabled) => setDraft((d) => ({ ...d, enabled }))}
          />
        </div>

        {/* Idle timeout */}
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <Label htmlFor="kiosk-idle">Idle timeout</Label>
            <p className="text-sm text-muted-foreground">
              How long before the screensaver appears.
            </p>
          </div>
          <Select
            value={String(draft.idleTimeoutSec)}
            onValueChange={(v) => setDraft((d) => ({ ...d, idleTimeoutSec: Number(v) }))}
          >
            <SelectTrigger id="kiosk-idle" className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {IDLE_PRESETS.map((preset) => (
                <SelectItem key={preset.value} value={String(preset.value)}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Screensaver style */}
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <Label htmlFor="kiosk-style">Screensaver style</Label>
            <p className="text-sm text-muted-foreground">
              An ambient clock, or a plain black screen.
            </p>
          </div>
          <Select
            value={draft.screensaver}
            onValueChange={(v) =>
              setDraft((d) => ({ ...d, screensaver: v as KioskSettings["screensaver"] }))
            }
          >
            <SelectTrigger id="kiosk-style" className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="clock">Ambient clock</SelectItem>
              <SelectItem value="blank">Black screen</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Wake sensor */}
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <Label htmlFor="kiosk-sensor">Wake sensor</Label>
            <p className="text-sm text-muted-foreground">
              Wake the display when a Homey motion or presence sensor fires.
            </p>
          </div>
          <Select
            value={draft.wakeSensorDeviceId ?? NONE_VALUE}
            onValueChange={(v) =>
              setDraft((d) => ({
                ...d,
                wakeSensorDeviceId: v === NONE_VALUE ? null : v,
              }))
            }
          >
            <SelectTrigger id="kiosk-sensor" className="w-[200px]">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>None</SelectItem>
              {wakeSensorDevices.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Wake on camera */}
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <Label htmlFor="kiosk-camera">Wake on camera motion</Label>
            <p className="text-sm text-muted-foreground">
              Use the device camera to detect motion. Requires camera permission and only runs while
              the screensaver is showing.
            </p>
          </div>
          <Switch
            id="kiosk-camera"
            checked={draft.wakeOnCamera}
            onCheckedChange={(wakeOnCamera) => setDraft((d) => ({ ...d, wakeOnCamera }))}
          />
        </div>

        <div className="flex justify-end">
          <Button disabled={save.isPending} onClick={() => save.mutate(draft)}>
            {save.isPending && <Loader2 className="animate-spin" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default KioskSettingsSection;
